/**
 * Forms Hub > Send: when the send button may be pressed, and the client picker's choices.
 * Pure helpers (no React) so the rules are unit tested.
 */

/** A calendar day in this browser (the user's local day), as YYYY-MM-DD. */
export const localDay = (d: Date = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export interface SendDraft {
  patientId: string;
  what: 'bundles' | 'forms';
  /** A bundle is picked (bundles mode). */
  bundlePicked: boolean;
  /** How many forms the picked bundle holds. */
  bundleForms: number;
  /** How many individual forms are picked (forms mode). */
  formsPicked: number;
  /** YYYY-MM-DD or '' (no due date). */
  dueDate: string;
  timing: 'now' | 'later';
  /** YYYY-MM-DD / HH:MM, used when timing is 'later'. */
  sendDate: string;
  sendTime: string;
}

/**
 * Why the send can't go yet, in a short sentence, or null when it can. The button is disabled
 * and this shows next to it.
 */
export function sendBlockReason(d: SendDraft, now: Date = new Date()): string | null {
  // credifyfbs#312: never send nothing (an empty bundle, or no form picked).
  if (d.what === 'bundles') {
    if (!d.bundlePicked) return 'Pick a bundle to send.';
    if (d.bundleForms < 1) return 'This bundle has no forms. Add a form to it first.';
  } else if (d.formsPicked < 1) {
    return 'Pick at least one form to send.';
  }
  if (!d.patientId) return 'Pick a client.';
  // credifyfbs#308: only today or later (the user's local today).
  const today = localDay(now);
  if (d.dueDate && d.dueDate < today) return 'The due date is in the past. Pick today or later.';
  if (d.timing === 'later') {
    if (!d.sendDate || !d.sendTime) return 'Set a send date and time.';
    if (d.sendDate < today) return 'The send date is in the past. Pick today or later.';
    const when = new Date(`${d.sendDate}T${d.sendTime}`);
    if (isNaN(when.getTime()) || when.getTime() <= now.getTime())
      return 'The send time has passed. Pick a later time.';
    if (d.dueDate && d.dueDate < d.sendDate) return 'The due date is before the send date.';
  }
  return null;
}

export interface ClientRow {
  id: string;
  firstName: string;
  lastName: string;
  mrn?: string | null;
}

/** The Client ID the Contacts list shows: the MRN, else the first 8 of the id (ContactsTab). */
export const clientIdOf = (c: Pick<ClientRow, 'id' | 'mrn'>): string =>
  c.mrn?.trim() || c.id.slice(0, 8).toUpperCase();

const fullName = (c: ClientRow) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();

/** credifyfbs#309: each client once (the same id twice is dropped). */
export function uniqueClients<T extends { id: string }>(list: T[]): T[] {
  const seen = new Set<string>();
  return list.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

/**
 * credifyfbs#309: the client picker's choices. Each client once; two clients with the same name
 * also show their Client ID (as in the Contacts list), so staff can tell them apart.
 */
export function clientOptions(
  list: ClientRow[],
): { value: string; label: string; search: string }[] {
  const clients = uniqueClients(list);
  const count = new Map<string, number>();
  for (const c of clients) {
    const k = fullName(c).toLowerCase();
    count.set(k, (count.get(k) ?? 0) + 1);
  }
  return clients.map((c) => {
    const name = fullName(c) || 'Unnamed client';
    const same = (count.get(fullName(c).toLowerCase()) ?? 0) > 1;
    return {
      value: c.id,
      label: same ? `${name} (Client ID ${clientIdOf(c)})` : name,
      search: clientIdOf(c),
    };
  });
}

/**
 * ehr#673 (ruled 2026-10-28): clinicians and supervisors send only to the clients they can open
 * (owned or shared). Their client list comes from GET /contacts, which the server already limits
 * to those clients, so the same-name Client ID hint above counts only clients they can see. The
 * server checks every send again and is the source of truth.
 */
export const isScopedSender = (role: string | undefined): boolean =>
  role === 'clinician' || role === 'supervisor';

/** The client picker's empty text: a clinician or supervisor picks from their own clients. */
export const clientPlaceholder = (role: string | undefined): string =>
  isScopedSender(role) ? '— Select one of your clients —' : '— Select a client —';

/**
 * ehr#673: the server refused the client (403 NOT_YOUR_CLIENT) or a scheduled send this user may
 * not change (403 NOT_YOUR_SEND). Its message says why; the rest of a multi-form send is not tried.
 */
export function isClientRefusal(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const { status, code } = e as { status?: unknown; code?: unknown };
  return status === 403 && (code === 'NOT_YOUR_CLIENT' || code === 'NOT_YOUR_SEND');
}
